import React, { useState } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, Loader2, Download } from 'lucide-react';
import { useContent } from '../contexts/SupabaseContentContext';
import { processContentForYouTube } from '../utils/contentProcessor';

interface WordPressPost {
  title: string;
  content: string;
  excerpt: string;
  status: string;
  date: string;
  slug: string;
  categories: string[];
  tags: string[];
  featured_image?: string;
  post_type: string;
  order?: number;
}

interface ImportStats {
  total: number;
  imported: number;
  skipped: number;
  errors: number;
  blogs: number;
  docs: number;
}

const WordPressImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const { addBlogPost, addDocPost, getCategoriesByType, addCategory, refreshData } = useContent();

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const parseWordPressXML = (xmlContent: string): WordPressPost[] => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    
    const items = xmlDoc.querySelectorAll('item');
    const posts: WordPressPost[] = [];

    items.forEach(item => {
      const postType = item.querySelector('wp\\:post_type, post_type')?.textContent;
      
      // Import cả posts và docs
      if (postType === 'post' || postType === 'docs') {
        const title = item.querySelector('title')?.textContent || '';
        let content = item.querySelector('content\\:encoded, encoded')?.textContent || '';
        const excerpt = item.querySelector('excerpt\\:encoded, excerpt')?.textContent || '';
        const status = item.querySelector('wp\\:status, status')?.textContent || 'draft';
        const date = item.querySelector('wp\\:post_date, post_date')?.textContent || '';
        
        // Lấy slug từ WordPress, bao gồm cả ID nếu có
        const wpPostName = item.querySelector('wp\\:post_name, post_name')?.textContent || '';
        const wpPostId = item.querySelector('wp\\:post_id, post_id')?.textContent || '';
        
        // Giữ nguyên slug gốc từ WordPress, bao gồm cả ID nếu có trong link
        let slug = wpPostName;
        
        // Nếu có link element, ưu tiên lấy slug từ link
        const linkElement = item.querySelector('link');
        if (linkElement && linkElement.textContent) {
          const linkUrl = linkElement.textContent;
          // Lấy phần cuối của URL (sau dấu / cuối cùng)
          const urlParts = linkUrl.split('/').filter(Boolean);
          if (urlParts.length > 0) {
            slug = urlParts[urlParts.length - 1];
          }
        }
        
        // Nếu không có slug, tạo từ tiêu đề
        if (!slug) {
          slug = generateSlug(title);
        }

        // Xử lý nội dung để chuyển đổi URL YouTube thành trình phát
        content = processContentForYouTube(content);

        // Lấy categories - xử lý cả category thường và doc_category
        const categoryElements = item.querySelectorAll('category[domain="category"], category[domain="doc_category"]');
        const categories: string[] = [];
        categoryElements.forEach(cat => {
          const catName = cat.textContent;
          if (catName) categories.push(catName);
        });

        // Lấy tags (chỉ cho blog posts)
        const tagElements = item.querySelectorAll('category[domain="post_tag"]');
        const tags: string[] = [];
        if (postType === 'post') {
          tagElements.forEach(tag => {
            const tagName = tag.textContent;
            if (tagName) tags.push(tagName);
          });
        }

        // Lấy menu_order cho docs (thứ tự hiển thị)
        const menuOrder = item.querySelector('wp\\:menu_order, menu_order')?.textContent;
        const order = menuOrder ? parseInt(menuOrder) : 1;

        // Lấy featured image nếu có
        let featuredImage = '';
        const attachmentUrl = item.querySelector('wp\\:attachment_url, attachment_url')?.textContent;
        if (attachmentUrl) {
          featuredImage = attachmentUrl;
        }

        // Tìm featured image trong content nếu không có attachment_url
        if (!featuredImage) {
          const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);
          if (imgMatch && imgMatch[1]) {
            featuredImage = imgMatch[1];
          }
        }

        posts.push({
          title,
          content,
          excerpt: excerpt || content.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
          status,
          date,
          slug,
          categories,
          tags,
          featured_image: featuredImage,
          post_type: postType,
          order: postType === 'docs' ? order : undefined
        });
      }
    });

    return posts;
  };

  const parseWordPressJSON = (jsonContent: string): WordPressPost[] => {
    try {
      const data = JSON.parse(jsonContent);
      const posts: WordPressPost[] = [];

      // Xử lý format JSON từ WordPress REST API
      if (Array.isArray(data)) {
        data.forEach(item => {
          if (item.type === 'post' || item.type === 'docs') {
            let content = item.content?.rendered || '';
            
            // Xử lý nội dung để chuyển đổi URL YouTube thành trình phát
            content = processContentForYouTube(content);

            // Lấy slug từ link nếu có
            let slug = item.slug || '';
            
            // Nếu có link, ưu tiên lấy slug từ link để giữ nguyên cả ID
            if (item.link) {
              const urlParts = item.link.split('/').filter(Boolean);
              if (urlParts.length > 0) {
                slug = urlParts[urlParts.length - 1];
              }
            }
            
            // Nếu không có slug, tạo từ tiêu đề
            if (!slug) {
              slug = generateSlug(item.title?.rendered || '');
            }

            posts.push({
              title: item.title?.rendered || '',
              content,
              excerpt: item.excerpt?.rendered || '',
              status: item.status === 'publish' ? 'publish' : 'draft',
              date: item.date || '',
              slug: slug,
              categories: item.categories || [],
              tags: item.tags || [],
              featured_image: item.featured_media_url || item.jetpack_featured_media_url || '',
              post_type: item.type,
              order: item.menu_order || 1
            });
          }
        });
      }

      return posts;
    } catch (error) {
      throw new Error('Invalid JSON format');
    }
  };

  const findOrCreateCategory = async (categoryName: string, type: 'blog' | 'doc') => {
    // Refresh data để có categories mới nhất
    await refreshData();
    
    const existingCategories = getCategoriesByType(type);
    
    // Tìm category đã tồn tại
    const existingCategory = existingCategories.find(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase()
    );

    if (existingCategory) {
      addLog(`Tìm thấy category có sẵn: ${categoryName} (${type})`);
      return existingCategory.id;
    }

    // Tạo category mới
    addLog(`Tạo category mới: ${categoryName} (${type})`);
    const newCategory = await addCategory({
      name: categoryName,
      slug: generateSlug(categoryName),
      description: `${categoryName}`,
      type: type,
      order: existingCategories.length + 1
    });

    if (newCategory) {
      addLog(`✓ Tạo category thành công: ${categoryName} (ID: ${newCategory.id})`);
      // Refresh lại data sau khi tạo category
      await refreshData();
      return newCategory.id;
    } else {
      addLog(`✗ Lỗi tạo category: ${categoryName}`);
      return null;
    }
  };

  const importPosts = async (posts: WordPressPost[]) => {
    const stats: ImportStats = { 
      total: posts.length, 
      imported: 0, 
      skipped: 0, 
      errors: 0,
      blogs: 0,
      docs: 0
    };
    
    addLog(`Bắt đầu import ${posts.length} bài viết...`);

    // Tạo tất cả categories trước
    const uniqueCategories = new Map<string, 'blog' | 'doc'>();
    posts.forEach(post => {
      if (post.categories.length > 0) {
        const categoryType = post.post_type === 'docs' ? 'doc' : 'blog';
        post.categories.forEach(catName => {
          if (!uniqueCategories.has(catName.toLowerCase())) {
            uniqueCategories.set(catName.toLowerCase(), categoryType);
          }
        });
      }
    });

    addLog(`Tìm thấy ${uniqueCategories.size} categories cần xử lý...`);

    // Tạo map để lưu category IDs
    const categoryMap = new Map<string, string>();

    for (const [categoryName, type] of uniqueCategories) {
      try {
        const categoryId = await findOrCreateCategory(categoryName, type);
        if (categoryId) {
          categoryMap.set(categoryName, categoryId);
        }
      } catch (error) {
        addLog(`✗ Lỗi xử lý category "${categoryName}": ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    addLog(`Đã xử lý xong categories. Bắt đầu import bài viết...`);

    // Import từng bài viết
    for (const post of posts) {
      try {
        // Bỏ qua bài viết không có tiêu đề
        if (!post.title.trim()) {
          stats.skipped++;
          addLog(`Bỏ qua bài viết không có tiêu đề`);
          continue;
        }

        const isDoc = post.post_type === 'docs';

        // Lấy category ID từ map đã tạo
        let categoryId = null;
        if (post.categories.length > 0) {
          const categoryKey = post.categories[0].toLowerCase();
          categoryId = categoryMap.get(categoryKey) || null;
          
          if (categoryId) {
            addLog(`Gán vào category: ${post.categories[0]} (ID: ${categoryId})`);
          } else {
            addLog(`⚠ Không tìm thấy category ID cho: ${post.categories[0]}`);
          }
        }

        if (isDoc) {
          // Tạo doc post
          const newDoc = await addDocPost({
            title: post.title,
            slug: post.slug,
            content: post.content,
            excerpt: post.excerpt,
            author: 'VMON',
            published: post.status === 'publish',
            category_id: categoryId,
            order: post.order || 1
          });

          if (newDoc) {
            stats.imported++;
            stats.docs++;
            addLog(`✓ Import doc thành công: ${post.title} ${categoryId ? `(Category: ${post.categories[0]})` : '(Không có category)'}`);
          } else {
            stats.errors++;
            addLog(`✗ Lỗi import doc: ${post.title}`);
          }
        } else {
          // Tạo blog post
          const newPost = await addBlogPost({
            title: post.title,
            slug: post.slug,
            content: post.content,
            excerpt: post.excerpt,
            author: 'VMON',
            published: post.status === 'publish',
            tags: post.tags,
            featured_image: post.featured_image || 'https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg',
            category_id: categoryId
          });

          if (newPost) {
            stats.imported++;
            stats.blogs++;
            addLog(`✓ Import blog thành công: ${post.title} ${categoryId ? `(Category: ${post.categories[0]})` : '(Không có category)'}`);
          } else {
            stats.errors++;
            addLog(`✗ Lỗi import blog: ${post.title}`);
          }
        }

        // Cập nhật stats
        setImportStats({ ...stats });

        // Delay nhỏ để tránh quá tải
        await new Promise(resolve => setTimeout(resolve, 200));

      } catch (error) {
        stats.errors++;
        addLog(`✗ Lỗi import "${post.title}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        setImportStats({ ...stats });
      }
    }

    // Refresh data cuối cùng để cập nhật UI
    await refreshData();

    addLog(`🎉 Hoàn thành import! Thành công: ${stats.imported} (${stats.blogs} blogs, ${stats.docs} docs), Lỗi: ${stats.errors}, Bỏ qua: ${stats.skipped}`);
    return stats;
  };

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setError('');
    setImportStats(null);
    setLogs([]);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError('');
    setLogs([]);
    setImportStats(null);

    try {
      const fileContent = await file.text();
      let posts: WordPressPost[] = [];

      if (file.name.endsWith('.xml')) {
        addLog('Đang phân tích file XML...');
        posts = parseWordPressXML(fileContent);
      } else if (file.name.endsWith('.json')) {
        addLog('Đang phân tích file JSON...');
        posts = parseWordPressJSON(fileContent);
      } else {
        throw new Error('Chỉ hỗ trợ file .xml hoặc .json');
      }

      if (posts.length === 0) {
        throw new Error('Không tìm thấy bài viết nào trong file');
      }

      const blogPosts = posts.filter(p => p.post_type === 'post');
      const docPosts = posts.filter(p => p.post_type === 'docs');

      addLog(`Tìm thấy ${posts.length} bài viết (${blogPosts.length} blogs, ${docPosts.length} docs)`);
      addLog(`Đã xử lý ${posts.filter(p => p.content.includes('youtube.com/embed/')).length} video YouTube`);
      
      const finalStats = await importPosts(posts);
      setImportStats(finalStats);

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi import');
      addLog(`Lỗi: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleFile = () => {
    const sampleData = {
      instructions: "Đây là file mẫu cho import WordPress. Thay thế dữ liệu này bằng dữ liệu thực từ WordPress của bạn.",
      posts: [
        {
          title: "Bài viết blog mẫu với video YouTube",
          content: "<p>Nội dung bài viết blog mẫu</p><p>Video hướng dẫn: https://www.youtube.com/watch?v=dQw4w9WgXcQ</p>",
          excerpt: "Tóm tắt bài viết blog mẫu",
          status: "publish",
          date: "2024-01-01",
          slug: "bai-viet-blog-mau-12345",
          link: "https://example.com/bai-viet-blog-mau-12345",
          categories: ["Tin tức"],
          tags: ["mẫu", "demo"],
          post_type: "post"
        },
        {
          title: "Tài liệu hướng dẫn mẫu",
          content: "<p>Nội dung tài liệu hướng dẫn mẫu</p><p>Video demo: https://youtu.be/dQw4w9WgXcQ</p>",
          excerpt: "Tóm tắt tài liệu mẫu",
          status: "publish",
          date: "2024-01-01",
          slug: "tai-lieu-huong-dan-mau",
          link: "https://example.com/docs/tai-lieu-huong-dan-mau",
          categories: ["VPS Windows"],
          tags: [],
          post_type: "docs",
          order: 1
        }
      ]
    };

    const blob = new Blob([JSON.stringify(sampleData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordpress-import-sample.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Import từ WordPress</h3>
          <p className="text-gray-600">Import bài viết và tài liệu từ WordPress XML export hoặc JSON file</p>
          <p className="text-sm text-blue-600 mt-1">✨ Tự động chuyển đổi URL YouTube thành trình phát video</p>
        </div>
        <button
          onClick={downloadSampleFile}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Download className="h-4 w-4 mr-2" />
          Tải file mẫu
        </button>
      </div>

      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {file ? (
          <div className="flex items-center justify-center">
            <FileText className="h-8 w-8 text-blue-600 mr-3" />
            <div className="text-left">
              <p className="font-medium text-gray-900">{file.name}</p>
              <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
        ) : (
          <div>
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Kéo thả file vào đây hoặc click để chọn
            </p>
            <p className="text-gray-500 mb-4">
              Hỗ trợ file .xml (WordPress Export) hoặc .json
            </p>
            <input
              type="file"
              accept=".xml,.json"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              Chọn file
            </label>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Import Button */}
      {file && !importing && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleImport}
            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="h-5 w-5 mr-2" />
            Bắt đầu Import
          </button>
        </div>
      )}

      {/* Import Progress */}
      {importing && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin mr-2" />
            <span className="font-medium text-blue-900">Đang import...</span>
          </div>
          {importStats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">{importStats.total}</div>
                <div className="text-sm text-gray-600">Tổng</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{importStats.imported}</div>
                <div className="text-sm text-gray-600">Thành công</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{importStats.blogs}</div>
                <div className="text-sm text-gray-600">Blogs</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{importStats.docs}</div>
                <div className="text-sm text-gray-600">Docs</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">{importStats.skipped}</div>
                <div className="text-sm text-gray-600">Bỏ qua</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{importStats.errors}</div>
                <div className="text-sm text-gray-600">Lỗi</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Results */}
      {importStats && !importing && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="font-medium text-green-900">Import hoàn thành!</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{importStats.total}</div>
              <div className="text-sm text-gray-600">Tổng</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{importStats.imported}</div>
              <div className="text-sm text-gray-600">Thành công</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{importStats.blogs}</div>
              <div className="text-sm text-gray-600">Blogs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{importStats.docs}</div>
              <div className="text-sm text-gray-600">Docs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{importStats.skipped}</div>
              <div className="text-sm text-gray-600">Bỏ qua</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{importStats.errors}</div>
              <div className="text-sm text-gray-600">Lỗi</div>
            </div>
          </div>
        </div>
      )}

      {/* Import Logs */}
      {logs.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Log Import:</h4>
          <div className="bg-gray-50 border rounded-lg p-4 max-h-64 overflow-y-auto">
            {logs.map((log, index) => (
              <div key={index} className="text-sm text-gray-700 font-mono mb-1">
                {log}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-medium text-yellow-900 mb-2">Hướng dẫn:</h4>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• <strong>WordPress XML:</strong> Vào WordPress Admin → Tools → Export → All content</li>
          <li>• <strong>WordPress JSON:</strong> Sử dụng WordPress REST API hoặc plugin export JSON</li>
          <li>• <strong>Blogs:</strong> post_type = "post" sẽ được import vào phần Blog</li>
          <li>• <strong>Docs:</strong> post_type = "docs" sẽ được import vào phần Tài liệu</li>
          <li>• <strong>Categories:</strong> Sẽ tự động tạo categories mới nếu chưa tồn tại</li>
          <li>• <strong>doc_category:</strong> Được xử lý như category cho docs</li>
          <li>• <strong>YouTube Videos:</strong> URL YouTube sẽ tự động chuyển thành trình phát nhúng</li>
          <li>• <strong>Images:</strong> Cần upload lại ảnh thủ công sau khi import</li>
          <li>• <strong>Slug:</strong> Sẽ giữ nguyên slug gốc từ WordPress để đảm bảo SEO</li>
          <li>• <strong>Category Assignment:</strong> Bài viết sẽ được tự động gán vào đúng thư mục</li>
          <li>• <strong>Author:</strong> Tất cả bài viết sẽ được gán tác giả là "VMON"</li>
        </ul>
      </div>
    </div>
  );
};

export default WordPressImport;